import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Checkbox,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ColorPalette from '../../../colorpalette/ColorPalette';
import CustomButton from '../../../components/buttons/CustomButton';

const RecipientList = ({
  recipients,
  selectedRecipients,
  onRecipientSelectionChange,
  onSelectAll,
  onSendToRecipient,
  messageTemplate,
  messageVariables,
  sendingMethod
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRecipient, setExpandedRecipient] = useState(null);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle select all checkbox
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Only select valid recipients
      const validRecipientIds = recipients
        .filter(recipient => isRecipientValid(recipient))
        .map(recipient => recipient.id);
      
      // Call the parent's onSelectAll with the valid recipient IDs
      if (validRecipientIds.length > 0) {
        // We need to pass true to select all valid recipients
        onSelectAll(true, validRecipientIds);
      }
    } else {
      // Deselect all
      onSelectAll(false);
    }
  };

  // Handle individual recipient selection
  const handleRecipientSelection = (recipientId, event) => {
    const recipient = recipients.find(r => r.id == recipientId);
    if (recipient && isRecipientValid(recipient)) {
      onRecipientSelectionChange(recipientId, event.target.checked);
    }
  };
  
  // Check if recipient is valid for the current sending method
  const isRecipientValid = (recipient) => {
    if (sendingMethod == 'whatsapp_api') {
      return recipient.phone && recipient.phone.trim() != '';
    } else if (sendingMethod == 'email') {
      return recipient.email && recipient.email.trim() != '';
    }
    return true;
  };

  // Toggle recipient details
  const toggleRecipientDetails = (recipientId) => {
    setExpandedRecipient(expandedRecipient == recipientId ? null : recipientId);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    
    const parts = date.split('-');
    if (parts.length != 3) return date;
    
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    
    const parts = time.split(':');
    if (parts.length < 2) return time;
    
    return `${parts[0]}:${parts[1]}`;
  };

  // Preview message for a specific recipient
  const previewMessage = (recipient) => {
    if (!messageTemplate) return '';
    
    let preview = messageTemplate;
    
    // Replace variables with recipient values
    const replacements = {
      '{name}': recipient.name ? recipient.name.split(' ')[0] : '',
      '{fullName}': recipient.name || '',
      '{greetings}': getTimeBasedGreeting(),
      '{doctor_name}': messageVariables.doctor_name || '',
      '{company_name}': messageVariables.company_name || '',
    };
    
    // Add appointment-specific variables if available
    if (recipient.appointment_date) {
      replacements['{appointment_date}'] = formatDate(recipient.appointment_date);
    }
    
    if (recipient.appointment_time) {
      replacements['{appointment_time}'] = formatTime(recipient.appointment_time);
    }
    
    // Replace all variables in the template
    Object.entries(replacements).forEach(([variable, value]) => {
      preview = preview.replace(new RegExp(variable, 'g'), value);
    });
    
    return preview;
  };

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Bom dia";
    } else if (hour >= 12 && hour < 18) {
      return "Boa tarde";
    } else {
      return "Boa noite";
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: ColorPalette.white,
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Table
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0 4px'
          }}
          size="small"
          stickyHeader
        >
          <TableHead>
            <TableRow sx={{
              height: '32px',
              backgroundColor: ColorPalette.white,
              '& th': {
                backgroundColor: ColorPalette.white,
                borderBottom: `2px solid ${ColorPalette.blueLight3}`,
                fontWeight: 600
              }
            }}>
              <TableCell padding="checkbox" sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'left',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent'
              }}>
                {(() => {
                  const validRecipients = recipients.filter(r => isRecipientValid(r));
                  const validSelectedCount = selectedRecipients.filter(id =>
                    validRecipients.some(r => r.id == id)
                  ).length;
                  
                  const isIndeterminate = validSelectedCount > 0 && validSelectedCount < validRecipients.length;
                  const isChecked = validRecipients.length > 0 && validSelectedCount == validRecipients.length;
                  
                  return (
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isChecked}
                      onChange={handleSelectAll}
                      sx={{
                        color: ColorPalette.blueMain,
                        '&.Mui-checked': {
                          color: ColorPalette.blueMain,
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: ColorPalette.blueMain,
                        }
                      }}
                    />
                  );
                })()}
              </TableCell>
              <TableCell sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'left',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent',
                fontSize: '0.8125rem'
              }}>Nome</TableCell>
              <TableCell sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'left',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent',
                width: '120px',
                fontSize: '0.8125rem'
              }}>Telefone</TableCell>
              <TableCell sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'left',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent',
                fontSize: '0.8125rem'
              }}>Email</TableCell>
              <TableCell sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'center',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent',
                width: '50px',
                fontSize: '0.8125rem'
              }}>Detalhes</TableCell>
              <TableCell sx={{
                color: ColorPalette.textMedium,
                fontWeight: 500,
                textAlign: 'center',
                padding: '4px 8px',
                borderBottom: `1px solid ${ColorPalette.border}`,
                backgroundColor: 'transparent',
                width: '50px',
                fontSize: '0.8125rem'
              }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recipients
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((recipient, index) => (
                <React.Fragment key={recipient.id}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: index % 2 == 0 ? ColorPalette.white : ColorPalette.blueLight4,
                      '&:hover': {
                        backgroundColor: ColorPalette.blueAlpha2
                      }
                    }}
                  >
                    <TableCell padding="checkbox" sx={{
                      padding: '6px 8px',
                      borderTopLeftRadius: '6px',
                      borderBottomLeftRadius: expandedRecipient == recipient.id ? '0' : '6px'
                    }}>
                      <Checkbox
                        checked={selectedRecipients.includes(recipient.id)}
                        onChange={(event) => handleRecipientSelection(recipient.id, event)}
                        disabled={!isRecipientValid(recipient)}
                        size="small"
                        sx={{
                          color: ColorPalette.blueMain,
                          padding: '2px',
                          '&.Mui-checked': {
                            color: ColorPalette.blueMain,
                          },
                          '&.Mui-disabled': {
                            color: `${ColorPalette.textLight} !important`,
                            opacity: 1
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{
                      padding: '6px 8px',
                      fontWeight: 'normal',
                      color: !isRecipientValid(recipient) ? ColorPalette.textLight : ColorPalette.textDark,
                      fontSize: '0.8125rem'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {!isRecipientValid(recipient) && (
                          <WarningAmberIcon
                            fontSize="small"
                            sx={{
                              color: ColorPalette.textLight,
                              flexShrink: 0,
                              fontSize: '1rem'
                            }}
                          />
                        )}
                        <span>{recipient.name}</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      padding: '6px 8px',
                      color: sendingMethod == 'whatsapp_api' && (!recipient.phone || recipient.phone.trim() == '')
                        ? ColorPalette.error
                        : ColorPalette.textMedium,
                      width: '120px',
                      fontSize: '0.8125rem'
                    }}>
                      {(sendingMethod == 'whatsapp_api' && (!recipient.phone || recipient.phone.trim() == '')) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <WarningAmberIcon
                            fontSize="small"
                            sx={{ color: ColorPalette.textLight, flexShrink: 0, fontSize: '0.875rem' }}
                          />
                          <Typography variant="caption" sx={{ color: ColorPalette.textLight, fontSize: '0.75rem' }}>
                            Necessário
                          </Typography>
                        </Box>
                      ) : (
                        recipient.phone || '-'
                      )}
                    </TableCell>
                    <TableCell sx={{
                      padding: '6px 8px',
                      color: sendingMethod == 'email' && (!recipient.email || recipient.email.trim() == '')
                        ? ColorPalette.error
                        : ColorPalette.textMedium,
                      fontSize: '0.8125rem'
                    }}>
                      {(sendingMethod == 'email' && (!recipient.email || recipient.email.trim() == '')) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <WarningAmberIcon
                            fontSize="small"
                            sx={{ color: ColorPalette.textLight, flexShrink: 0, fontSize: '0.875rem' }}
                          />
                          <Typography variant="caption" sx={{ color: ColorPalette.textLight, fontSize: '0.75rem' }}>
                            Necessário
                          </Typography>
                        </Box>
                      ) : (
                        recipient.email || '-'
                      )}
                    </TableCell>
                    <TableCell sx={{
                      padding: '6px 4px',
                      textAlign: 'center',
                      width: '50px'
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRecipientDetails(recipient.id)}
                        sx={{
                          color: ColorPalette.blueMain,
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: ColorPalette.blueAlpha
                          }
                        }}
                      >
                        {expandedRecipient == recipient.id ?
                          <ExpandLessIcon sx={{ fontSize: '1.125rem' }} /> :
                          <ExpandMoreIcon sx={{ fontSize: '1.125rem' }} />
                        }
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{
                      padding: '6px 4px',
                      textAlign: 'center',
                      borderTopRightRadius: '6px',
                      borderBottomRightRadius: expandedRecipient == recipient.id ? '0' : '6px',
                      width: '50px'
                    }}>
                      <Tooltip title="Enviar mensagem individual">
                        <span>
                          <CustomButton
                            style="secondary"
                            icon={<SendIcon sx={{ fontSize: '0.875rem' }} />}
                            onClick={() => onSendToRecipient(recipient.id)}
                            rounded={true}
                            outlined={true}
                            disabled={!messageTemplate}
                            sx={{
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: '0'
                            }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: 'transparent' }}>
                    <TableCell colSpan={6} sx={{
                      padding: 0,
                      border: 0,
                      backgroundColor: index % 2 == 0 ? ColorPalette.white : ColorPalette.blueLight4,
                      borderBottomLeftRadius: expandedRecipient == recipient.id ? '8px' : '0',
                      borderBottomRightRadius: expandedRecipient == recipient.id ? '8px' : '0'
                    }}>
                      <Collapse in={expandedRecipient == recipient.id} timeout="auto" unmountOnExit>
                        <Box sx={{
                          margin: '8px',
                          backgroundColor: ColorPalette.bgMain,
                          padding: '12px',
                          borderRadius: '8px',
                          border: `1px solid ${ColorPalette.border}`
                        }}>
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 600,
                            color: ColorPalette.blueDark,
                            marginBottom: '8px',
                            fontSize: '0.8125rem'
                          }}>
                            Pré-visualização da Mensagem
                          </Typography>
                          
                          <Box sx={{
                            backgroundColor: ColorPalette.bgMain,
                            borderRadius: '8px',
                            minHeight: '80px',
                            border: `1px solid ${ColorPalette.border}`,
                            whiteSpace: 'pre-wrap',
                            position: 'relative',
                            marginBottom: '12px',
                            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%235a80fb\' fill-opacity=\'0.03\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
                            backgroundSize: '100px 100px'
                          }}>
                            <Box sx={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '100%',
                              height: '24px',
                              backgroundColor: ColorPalette.secondary,
                              borderTopLeftRadius: '8px',
                              borderTopRightRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0 8px'
                            }}>
                              <Typography variant="caption" sx={{ color: ColorPalette.white, fontWeight: 600, fontSize: '0.6875rem' }}>
                                WhatsApp
                              </Typography>
                            </Box>
                            
                            <Box sx={{ marginTop: '28px', padding: '4px 8px' }}>
                              <Box sx={{
                                backgroundColor: ColorPalette.white,
                                padding: '8px',
                                borderRadius: '6px',
                                maxWidth: '80%',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                position: 'relative',
                                '&:after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: '6px',
                                  right: '-4px',
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: ColorPalette.white,
                                  transform: 'rotate(45deg)',
                                  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.05)'
                                }
                              }}>
                                <Typography variant="body2" sx={{ color: ColorPalette.textDark, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                                  {previewMessage(recipient)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: ColorPalette.textLight, display: 'block', textAlign: 'right', marginTop: '2px', fontSize: '0.6875rem' }}>
                                  {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          
                          {(recipient.appointment_date || recipient.appointment_time) && (
                            <Box>
                              <Typography variant="subtitle2" sx={{
                                fontWeight: 600,
                                color: ColorPalette.blueDark,
                                marginBottom: '4px',
                                fontSize: '0.75rem'
                              }}>
                                Informações da Consulta
                              </Typography>
                              <Box sx={{
                                display: 'flex',
                                gap: '12px',
                                backgroundColor: ColorPalette.white,
                                padding: '6px 8px',
                                borderRadius: '6px',
                                border: `1px solid ${ColorPalette.border}`
                              }}>
                                {recipient.appointment_date && (
                                  <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontSize: '0.75rem' }}>
                                    <strong>Data:</strong> {formatDate(recipient.appointment_date)}
                                  </Typography>
                                )}
                                {recipient.appointment_time && (
                                  <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontSize: '0.75rem' }}>
                                    <strong>Horário:</strong> {formatTime(recipient.appointment_time)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </Paper>
      
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        paddingBlock: '4px',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '4px'
      }}>
        <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontSize: '0.75rem', fontWeight: 500 }}>
          Página {page + 1} de {Math.ceil(recipients.length / rowsPerPage)} ({recipients.length} contatos)
        </Typography>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={recipients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Por página:"
          sx={{
            '.MuiTablePagination-toolbar': {
              padding: 0,
              minHeight: '36px'
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              margin: 0,
              fontSize: '0.75rem'
            },
            '.MuiTablePagination-select': {
              paddingTop: 0,
              paddingBottom: 0
            },
            '.MuiTablePagination-actions': {
              marginLeft: '8px'
            },
            '& .MuiIconButton-root': {
              padding: '4px'
            },
            '& .Mui-selected': {
              backgroundColor: '#5A80FB !important',
              color: '#FFFFFF',
            },
            '& .MuiPaginationItem-root:hover': {
              backgroundColor: 'rgba(90, 128, 251, 0.1)',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RecipientList;